import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PriceCalcPricingSource } from '@hero/api';
import { cn } from '@hero/web-lib';

/**
 * The four buckets a price can sit in, as defined by
 * `getDisplayPriceCategory` in `@hero/core`. Anything that shows or edits a
 * price labels it with one of these — never with the fine-grained persisted
 * strategy.
 */
export type PriceBadgeCategory = 'agreement' | 'everyone' | 'one_time' | 'unresolved';

/**
 * Resolver output → the four display buckets. `seller_own` and `buyer_own`
 * both mean "a rate anyone on that side can get", which is what the user
 * reads as a public rate.
 */
export const priceCategoryFromSource = (source: PriceCalcPricingSource): PriceBadgeCategory => {
  switch (source) {
    case 'directed':
      return 'agreement';
    case 'seller_own':
    case 'buyer_own':
      return 'everyone';
    case 'one_time':
      return 'one_time';
    default:
      return 'unresolved';
  }
};

const TONE: Record<PriceBadgeCategory, { dot: string; chip: string }> = {
  agreement: { dot: 'bg-emerald-500', chip: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  everyone: { dot: 'bg-sky-500', chip: 'border-sky-200 bg-sky-50 text-sky-700' },
  one_time: { dot: 'bg-amber-500', chip: 'border-amber-200 bg-amber-50 text-amber-800' },
  unresolved: { dot: 'bg-rose-500', chip: 'border-rose-200 bg-rose-50 text-rose-700' },
};

const LABEL: Record<PriceBadgeCategory, { key: string; fallback: string }> = {
  agreement: { key: 'drawer.price_edit.agreement_price_badge', fallback: 'Agreement price' },
  everyone: { key: 'drawer.price_edit.everyone_price_badge', fallback: 'Public rate' },
  one_time: { key: 'drawer.price_edit.custom_price_badge', fallback: 'Custom price' },
  unresolved: { key: 'drawer.price_edit.unresolved_price_badge', fallback: 'Awaiting price' },
};

export interface PriceCategoryBadgeProps {
  /** Pass this when you already hold a display category. */
  category?: PriceBadgeCategory;
  /** Pass this when you hold a resolver source instead; it is mapped. */
  source?: PriceCalcPricingSource;
  size?: 'xs' | 'sm';
  className?: string;
}

/**
 * `<PriceCategoryBadge>` — says what a price *is* next to the amount.
 *
 * A number on its own is unreadable in a network where the same lane can be
 * priced four different ways: the user's first question about any amount is
 * whether it came from an agreement with this client, a published rate, a
 * one-off they typed, or nothing at all. This is the single place that
 * answers it, so the emerald chip means the same thing on the publish page,
 * the price-edit drawer, the location-change modal, and an agent card.
 *
 * Colour follows the four categories rather than the persisted strategy —
 * see the `getDisplayPriceCategory` rules in the workspace guide.
 */
export const PriceCategoryBadge: React.FC<PriceCategoryBadgeProps> = ({
  category,
  source,
  size = 'xs',
  className,
}) => {
  const { t } = useTranslation('m-order');
  const resolved = category ?? (source ? priceCategoryFromSource(source) : undefined);
  if (!resolved) return null;

  const tone = TONE[resolved];
  const label = LABEL[resolved];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-medium',
        size === 'xs' ? 'px-1.5 py-px text-[10px]' : 'px-2.5 py-1 text-[11px]',
        tone.chip,
        className,
      )}>
      <span className={cn('inline-block size-1.5 shrink-0 rounded-full', tone.dot)} />
      {t(label.key, { defaultValue: label.fallback })}
    </span>
  );
};
